import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"
import { Form, Button } from "antd"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import classes from "classnames"
// hooks
import useFormErrors from "hooks/useAntdFormErrors"
// ui
import { CustomInput, PhoneInput } from "ui/Input"
import { CustomForm } from "ui/Form"
// utils
import { CustomInputError } from "ui/CustomInputError"
import { notification } from "ui/Notification"
import useActiveGuide from "hooks/useActiveClient"
import { sendOtpToVerifyPhone, verifyOtpForPhoneNumberUpdation, sendOtpToVerifyEmail, verifyOtpForEmailUpdation } from "store/user"

export const ChangeEmailPhoneForm = ({ inputType = "email", hideActions = false }) => {
  const isEmail = inputType === "email"
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.auth)
  const { email, phone_number, country_code } = useSelector((state) => state.user)
  const [editingInput, setEditingInput] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState("")
  const [isValidValue, setSubmittable] = useState(false)
  const { errors } = useFormErrors(form)
  const { isActive: active } = useActiveGuide()
  // LIFT timer state up
  const [timer, setTimer] = useState(0)
  const [isSendCodeLoading, setisSendCodeLoading] = useState(false)
  const [isConfirmLoading, setisConfirmLoading] = useState(false)
  const onInputChange = () => {
    // Store the complete phone object to maintain both number and country code
    if (!editingInput) {
      setEditingInput(true)
    }
    const value = form.getFieldValue(inputType)
    setSubmittable(!!value)
  }

  // Add a ref to reset SendCodeButton state
  const sendCodeButtonResetRef = React.useRef(null)
  const handleCancel = () => {
    setEditingInput(false)
    setCodeSent(false)
    setTimer(0)
    setSubmittable(false)
    form.resetFields()
    if (sendCodeButtonResetRef.current) {
      sendCodeButtonResetRef.current()
    }
  }

  const handleSendCode = async () => {
    // Validate the input field before sending code
    try {
      await form.validateFields([inputType])
    } catch (e) {
      // Validation failed, show error notification
      console.log(e)
      return
    }
    const inputValue = form.getFieldValue(inputType)
    if (isEmail) {
      setisSendCodeLoading(true)
      dispatch(sendOtpToVerifyEmail(inputValue))
        .then((res) => {
          notification.success({
            message: res.message
          })
          setCodeSent(true)
          setTimer(60)
        })
        .catch(() => {})
        .finally(() => {
          setisSendCodeLoading(false)
        })
      return
    }
    if (inputValue) {
      setisSendCodeLoading(true)
      const payload = {
        phone_number: inputValue?.fullNumber,
        country_code: inputValue?.countryCode
      }
      dispatch(sendOtpToVerifyPhone(payload))
        .then((res) => {
          notification.success({
            message: t(`settings.otp_sent`)
          })
          setCodeSent(true)
          setTimer(60)
        })
        .catch(() => {})
        .finally(() => {
          setisSendCodeLoading(false)
        })
    }
  }

  const handleConfirmCode = () => {
    setisConfirmLoading(true)
    const inputValue = form.getFieldValue(inputType)
    const payload = {
      phone_number: inputValue?.fullNumber,
      country_code: inputValue?.countryCode,
      otp: form.getFieldValue("code")
    }
    if (isEmail) {
      payload.email = form.getFieldValue("email")
      dispatch(verifyOtpForEmailUpdation(payload))
        .then((res) => {
          notification.success({
            message: t(`settings.${inputType}_confirmed`)
          })
          setCodeSent(false)
          setSubmittable(false)
          setEditingInput(false)
          form.setFieldValue("code", "")
          setCode("")
          setTimer(0) // Reset timer so resend is not shown
          if (sendCodeButtonResetRef.current) {
            sendCodeButtonResetRef.current()
          }
        })
        .catch(() => {})
        .finally(() => {
          setisConfirmLoading(false)
        })
      return
    }
    dispatch(verifyOtpForPhoneNumberUpdation(payload))
      .then((res) => {
        notification.success({
          message: t(`settings.${inputType}_confirmed`)
        })
        setCodeSent(false)
        setSubmittable(false)
        setEditingInput(false)
        form.setFieldValue("code", "")
        setCode("")
        setTimer(0) // Reset timer so resend is not shown
        if (sendCodeButtonResetRef.current) {
          sendCodeButtonResetRef.current()
        }
      })
      .catch(() => {})
      .finally(() => {
        setisConfirmLoading(false)
      })
  }

  return (
    <CustomForm form={form} initialValues={{ email, phone_number }} layout="vertical">
      <Form.Item
        hidden={!isEmail}
        label={t("sign_up.email")}
        name="email"
        required
        autoComplete="off"
        rules={[
          { type: "email", message: t("form_validation.email") },
          { required: true, message: t("form_validation.cannot_be_blank") }
        ]}
        help={errors?.email ? <CustomInputError errorKey={errors.email} showLinkText={false} /> : null}
      >
        <CustomInput disabled={isEmail} placeholder="Enter Email" onChange={onInputChange} />
      </Form.Item>
      <Form.Item
        hidden={isEmail}
        label={t("guide.phone_number")}
        name="phone_number"
        required={true}
        rules={[
          {
            validator: (_, value) => {
              if (!value?.fullNumber) {
                return Promise.reject(t("form_validation.phone_required"))
              }
              return validatePhoneNumber(value?.fullNumber)
            }
          }
        ]}
      >
        <PhoneInput initialValue={phone_number} initialCountryCode={country_code} onChange={onInputChange} />
      </Form.Item>
      {!isEmail && !hideActions && (
        <div className={classes("flex gap-s mb-s", { hidden: !editingInput })}>
          <SendCodeButton
            onClick={handleSendCode}
            disabled={!isValidValue || isSendCodeLoading}
            timer={timer}
            setTimer={setTimer}
            loading={isSendCodeLoading}
            resetSendStateRef={sendCodeButtonResetRef}
          />
          <Button
            color="primary"
            variant="outlined"
            size="medium"
            onClick={active && editingInput ? handleCancel : undefined}
            disabled={!active || !editingInput}
          >
            {t("global.cancel")}
          </Button>
        </div>
      )}

      <div className={classes({ hidden: !editingInput || !codeSent })}>
        <Form.Item
          label={t("enter_code.title")}
          name="code"
          required
          autoComplete="off"
          rules={[
            { max: 5, message: t("form_validation.five_digit_code") },
            { required: true, message: t("enter_code.title") }
          ]}
          help={errors?.pin_code ? <CustomInputError errorKey={errors.pin_code} /> : null}
        >
          <CustomInput placeholder="XXXXX" onChange={(e) => setCode(e.target.value)} />
        </Form.Item>
        <Button
          className="mb-s"
          type="primary"
          size="medium"
          loading={isConfirmLoading || loading}
          disabled={!active || code.length !== 5 || isConfirmLoading}
          onClick={active && code.length === 5 ? handleConfirmCode : undefined}
        >
          {t("global.confirm")}
        </Button>
      </div>
    </CustomForm>
  )
}

const SendCodeButton = ({ onClick, disabled, timer, setTimer, loading, resetSendStateRef }) => {
  const { t } = useTranslation()
  const { isActive: active } = useActiveGuide()
  const [hasSent, setHasSent] = useState(false)
  const [isResend, setIsResend] = useState(false)

  useEffect(() => {
    let interval
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1)
      }, 1000)
    } else if (timer === 0 && hasSent) {
      clearInterval(interval)
    }
    return () => clearInterval(interval)
  }, [timer, setTimer, hasSent])

  // Expose reset function to parent
  useEffect(() => {
    if (resetSendStateRef) {
      resetSendStateRef.current = () => {
        setHasSent(false)
        setIsResend(false)
      }
    }
  }, [resetSendStateRef])

  const handleClick = () => {
    if (hasSent) {
      setIsResend(true)
    } else {
      setIsResend(false)
    }
    onClick()
    setHasSent(true)
    // setTimer(60) will be called only if validation passes in handleSendCode
  }

  let buttonText
  if (loading) {
    buttonText = isResend ? "Resend Code" : t("password_recovery.send_code")
  } else if (timer > 0) {
    buttonText = (
      <div className="flex">
        {t("enter_code.resend_in")}
        <div style={{ width: 25 }}>{timer}</div>
        {"sec."}
      </div>
    )
  } else if (hasSent) {
    buttonText = "Resend Code"
  } else {
    buttonText = t("password_recovery.send_code")
  }

  return (
    <Button
      loading={loading}
      type="primary"
      size="medium"
      onClick={active && !disabled && timer === 0 && !loading ? handleClick : undefined}
      disabled={!active || disabled || timer > 0 || loading}
    >
      {buttonText}
    </Button>
  )
}

SendCodeButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  timer: PropTypes.number,
  setTimer: PropTypes.func,
  loading: PropTypes.bool,
  resetSendStateRef: PropTypes.object
}
